import { Link } from "react-router-dom";
import { Car, ChevronRight, Droplets, Fuel } from "lucide-react";
import type { FuelUserSummary } from "@/lib/fuel-analytics";
import type { GasStation } from "@/types/database";
import { SafeImage } from "@/components/ui/SafeImage";
import { formatCurrency, formatDateTime, formatFuelPrice, formatNumber, imageSrc, userDisplayName } from "@/lib/utils";

function UserAvatar({ summary }: { summary: FuelUserSummary }) {
  const user = summary.user;
  const profilePhoto = user ? imageSrc(user.profilePhoto) : null;
  const name = user ? userDisplayName(user) : summary.userID.slice(0, 8);

  if (profilePhoto) {
    return (
      <SafeImage
        src={profilePhoto}
        alt={name}
        width={48}
        height={48}
        className="h-12 w-12 rounded-full object-cover"
      />
    );
  }

  const initials = user
    ? `${user.firstName?.charAt(0) ?? ""}${user.lastName?.charAt(0) ?? ""}`.toUpperCase() || "U"
    : "U";

  return (
    <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-full bg-accent-subtle text-sm font-bold text-accent-foreground">
      {initials}
    </div>
  );
}

function stationName(stations: GasStation[], stationId: string | null) {
  if (!stationId) return null;
  return stations.find((s) => s.stationID === stationId)?.name ?? null;
}

function UserFuelRow({ summary, stations }: { summary: FuelUserSummary; stations: GasStation[] }) {
  const user = summary.user;
  const name = user ? userDisplayName(user) : summary.userID.slice(0, 8);
  const topStation = stationName(stations, summary.topStationId);

  return (
    <Link
      to={`/fuel-activity/users/${summary.userID}`}
      className="group hover-raise flex items-center gap-4 rounded-2xl bg-canvas p-4 hover:bg-surface"
    >
      <UserAvatar summary={summary} />
      <div className="min-w-0 flex-1">
        <div className="flex items-center gap-2">
          <p className="truncate font-bold">{name}</p>
          {summary.topGrade ? (
            <span className="rounded-full bg-surface px-2 py-0.5 text-[10px] font-bold text-muted">
              {summary.topGrade}
            </span>
          ) : null}
        </div>
        <p className="mt-1 truncate text-sm text-muted">{user?.email ?? user?.country ?? summary.userID}</p>
        <p className="mt-1 text-xs text-muted">
          Last fill {summary.lastLoggedAt ? formatDateTime(summary.lastLoggedAt) : "—"}
          {topStation ? ` · ${topStation}` : ""}
        </p>
      </div>
      <div className="hidden shrink-0 gap-4 text-xs font-semibold text-muted sm:flex">
        <span className="inline-flex items-center gap-1">
          <Fuel className="h-3.5 w-3.5" />
          {formatNumber(summary.fillUps)}
        </span>
        <span className="inline-flex items-center gap-1">
          <Droplets className="h-3.5 w-3.5" />
          {formatNumber(Math.round(summary.totalLitres))} L
        </span>
        <span className="inline-flex items-center gap-1">
          <Car className="h-3.5 w-3.5" />
          {formatNumber(summary.carIds.length)}
        </span>
      </div>
      <div className="shrink-0 text-right">
        <p className="text-sm font-extrabold">{formatCurrency(summary.totalSpent)}</p>
        <p className="mt-0.5 text-xs text-muted">{formatFuelPrice(summary.avgPricePerLitre)}/L avg</p>
      </div>
      <ChevronRight className="h-4 w-4 shrink-0 text-muted opacity-0 transition group-hover:opacity-100" />
    </Link>
  );
}

export function FuelUserExplorer({
  summaries,
  stations,
}: {
  summaries: FuelUserSummary[];
  stations: GasStation[];
}) {
  if (summaries.length === 0) {
    return (
      <p className="rounded-2xl bg-canvas p-6 text-center text-sm text-muted">
        No fuel activity matches your filters yet.
      </p>
    );
  }

  return (
    <div className="space-y-2">
      {summaries.map((summary) => (
        <UserFuelRow key={summary.userID} summary={summary} stations={stations} />
      ))}
    </div>
  );
}
